import { Link } from 'react-router-dom';
import { Calendar, MoreHorizontal, Pencil } from 'lucide-react';
import { MemberByline } from './MemberByline';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { formatTimestampDate, formatTimestampDateTime } from '../../lib/dates';
import { bodyWithoutMatchingTitle, wikiRoute, type WikiPageFull, type WikiSpaceRef } from '../../lib/wiki';
import { useMembers } from './use-member-names';
import WikiMarkdownView from './WikiMarkdownView';

type Props = {
  space: WikiSpaceRef;
  spaceName: string;
  page: WikiPageFull;
  onEdit: () => void;
  onCopyLink: () => void;
  onMove: () => void;
  onCreateChild: () => void;
  onHistory: () => void;
  onDelete: () => void;
};

/** Screen 3: breadcrumbs, title, Edit / ⋯, byline, body, child pages. */
export default function WikiPageView({
  space,
  spaceName,
  page,
  onEdit,
  onCopyLink,
  onMove,
  onCreateChild,
  onHistory,
  onDelete,
}: Props) {
  const members = useMembers();
  const updatedDate = formatTimestampDate(page.updated_at) ?? page.updated_at;
  const updatedDateTime = formatTimestampDateTime(page.updated_at);

  return (
    <article className="min-w-0 flex-1 px-2 py-2 sm:px-6 sm:py-4">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <Link to={wikiRoute(space)} className="hover:underline">
          {spaceName}
        </Link>
        {page.ancestors.map((ancestor, index) => {
          const path = page.ancestors
            .slice(0, index + 1)
            .map((row) => row.slug)
            .join('/');
          return (
            <span key={ancestor.id} className="flex items-center gap-1">
              <span aria-hidden>›</span>
              <Link to={wikiRoute(space, path)} className="hover:underline">
                {ancestor.title}
              </Link>
            </span>
          );
        })}
      </nav>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <h1 className="min-w-0 break-words text-2xl font-semibold leading-8 tracking-tight">{page.title}</h1>
        <div className="flex shrink-0 items-center gap-1">
          <Button type="button" size="sm" title="Edit (E)" onClick={onEdit}>
            <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="icon" variant="outline" className="h-8 w-8" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onCopyLink}>Copy link</DropdownMenuItem>
              <DropdownMenuItem onSelect={onMove}>Move…</DropdownMenuItem>
              <DropdownMenuItem onSelect={onCreateChild}>Create child page</DropdownMenuItem>
              <DropdownMenuItem onSelect={onHistory}>History</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
        <MemberByline
          members={members}
          userId={page.updated_by_id}
          nameClassName="font-medium text-foreground"
        />
        <time
          className="inline-flex items-center gap-1.5"
          dateTime={page.updated_at}
          title={updatedDateTime ?? undefined}
        >
          <Calendar className="h-3.5 w-3.5" aria-hidden />
          <span className="sr-only">Updated </span>
          {updatedDate}
        </time>
        <Badge variant="secondary" className="font-normal tabular-nums" aria-label={`Version ${page.version}`}>
          v{page.version}
        </Badge>
      </div>

      <div className="mt-4 border-t pt-4">
        {page.body_md.trim().length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This page is empty.{' '}
            <button type="button" className="underline" onClick={onEdit}>
              Edit
            </button>{' '}
            to add content.
          </p>
        ) : (
          <WikiMarkdownView
            key={`${page.id}:${page.version}`}
            value={bodyWithoutMatchingTitle(page.body_md, page.title)}
          />
        )}
      </div>

      {page.children.length > 0 ? (
        <section className="mt-8 border-t pt-4" aria-labelledby="wiki-child-pages">
          <h2 id="wiki-child-pages" className="text-sm font-medium">
            Pages
          </h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {page.children.map((child) => (
              <li key={child.id}>
                <Link
                  to={wikiRoute(space, `${page.path}/${child.slug}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  {child.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
